
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';

interface ProductCardProps {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  image: string;
  brand?: string;
  rating?: number;
  inStock?: boolean;
  isNew?: boolean;
  discount?: number;
}

const ProductCard = ({
  id,
  name,
  price,
  originalPrice,
  image,
  brand,
  rating = 4.5,
  inStock = true,
  isNew = false,
  discount,
}: ProductCardProps) => {
  const { addToCart } = useCart();
  const [isAdding, setIsAdding] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsAdding(true);
    addToCart({ id, name, price, image, brand });
    setTimeout(() => setIsAdding(false), 500);
  };

  return (
    <Link
      to={`/product/${id}`}
      className="group bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden block cursor-pointer"
      data-product-shop
    >
      {/* Image Container */}
      <div className="relative aspect-square overflow-hidden bg-gray-100">
        <img
          src={image}
          alt={name}
          className={`w-full h-full object-contain p-4 group-hover:scale-110 transition-transform duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setImageLoaded(true)}
        />
        {!imageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-[#0D47A1] border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {/* Badges */}
        <div className="absolute top-3 right-3 flex flex-col gap-2">
          {isNew && (
            <span className="bg-[#4CAF50] text-white text-xs px-2 py-1 rounded-full font-medium">
              جديد
            </span>
          )}
          {discount && (
            <span className="bg-[#FF8F00] text-white text-xs px-2 py-1 rounded-full font-medium">
              خصم {discount}%
            </span>
          )}
        </div>

        {/* Quick Actions */}
        <div className="absolute top-3 left-3 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button className="w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-[#0D47A1] hover:text-white transition-colors cursor-pointer">
            <i className="ri-heart-line text-sm"></i>
          </button>
          <button className="w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-[#0D47A1] hover:text-white transition-colors cursor-pointer">
            <i className="ri-eye-line text-sm"></i>
          </button>
        </div>

        {/* Out of Stock Overlay */}
        {!inStock && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="bg-white text-gray-800 px-4 py-2 rounded-lg font-medium">
              نفذت الكمية
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {brand && (
          <span className="text-xs text-[#0D47A1] font-medium">{brand}</span>
        )}
        <h3 className="font-medium text-gray-800 mt-1 line-clamp-2 min-h-[48px] group-hover:text-[#0D47A1] transition-colors">
          {name}
        </h3>

        {/* Rating */}
        <div className="flex items-center gap-1 mt-2">
          {[...Array(5)].map((_, i) => (
            <i
              key={i}
              className={`ri-star-${i < Math.floor(rating) ? 'fill' : 'line'} text-sm ${i < Math.floor(rating) ? 'text-[#FF8F00]' : 'text-gray-300'}`}
            ></i>
          ))}
          <span className="text-xs text-gray-500 mr-1">({rating})</span>
        </div>

        {/* Price */}
        <div className="flex items-center justify-between mt-3">
          <div>
            <span className="text-lg font-bold text-[#0D47A1]">
              {price.toLocaleString()} ر.س
            </span>
            {originalPrice && (
              <span className="text-sm text-gray-400 line-through mr-2">
                {originalPrice.toLocaleString()} ر.س
              </span>
            )}
          </div>
        </div>

        {/* Add to Cart Button */}
        <button
          onClick={handleAddToCart}
          disabled={!inStock}
          className={`w-full mt-3 py-2.5 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap ${
            inStock
              ? 'bg-[#0D47A1] text-white hover:bg-[#0a3a8a]'
              : 'bg-gray-200 text-gray-500 cursor-not-allowed'
          } ${isAdding ? 'scale-95' : ''}`}
        >
          {isAdding ? (
            <>
              <i className="ri-check-line"></i>
              تمت الإضافة
            </>
          ) : (
            <>
              <i className="ri-shopping-cart-2-line"></i>
              أضف للسلة
            </>
          )}
        </button>
      </div>
    </Link>
  );
};

export default ProductCard;
